import { Directory } from '@stratumn/atomic';

import graphql from 'babel-plugin-relay/macro';
import { ROUTE_BOT } from 'constant/routes';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { generatePath, withRouter } from 'react-router-dom';
import { withGqlClient } from 'wrappers';

import BotRow from './botRow';
import styles from './bots.style';

/**
 * Compare function to use with array.sort to sort bots edges
 * @param {object} edgeA The first edge bot element for comparison.
 * @param {object} edgeB The second edge bot element for comparison.
 * @returns -1, 1 or 0 depending on the bots name alphabetical order.
 */
const botsAlphabeticalCompareFn = (edgeA, edgeB) => {
  if (edgeA.node.name.toLowerCase() < edgeB.node.name.toLowerCase()) {
    return -1;
  }
  if (edgeA.node.name.toLowerCase() > edgeB.node.name.toLowerCase()) {
    return 1;
  }
  return 0;
};

const renderBotRow = (history, bots, node, index, organizationId) => (
  <BotRow
    bot={node}
    botsTotalCount={bots.totalCount}
    index={index}
    key={node.id}
    onClick={rowId =>
      history.push(
        generatePath(ROUTE_BOT, { id: rowId, orgId: organizationId })
      )
    }
  />
);

export function Bots({ history, organization: { bots, rowId } }) {
  const sortedBots = [...bots.edges].sort(botsAlphabeticalCompareFn);

  return (
    <Directory columnTitle="Name" columnTitle1="Teams" showColumnTitles>
      {sortedBots.map((edge, index) =>
        renderBotRow(history, bots, edge.node, index, rowId)
      )}
    </Directory>
  );
}

Bots.propTypes = {
  classes: PropType.object.isRequired,
  history: PropType.object.isRequired,
  organization: PropType.object.isRequired
};

/**
 * type ConnectionConfig
 */
const ConnectionConfig = {
  direction: 'forward',
  // These are the variables used to read the data from the fragment.
  getFragmentVariables(prevVars, totalCount) {
    return {
      ...prevVars,
      count: totalCount
    };
  },
  // These are the variables to use when sending the pagination query.
  getVariables(props, { count, cursor }, fragmentVariables) {
    return {
      cursor,
      count,
      orgRowId: fragmentVariables.orgRowId
    };
  },
  query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query botsQuery(
      $cursor: Cursor
      $orgRowId: BigInt!
      $botFilter: BotFilter
    ) {
      organizationByRowId(rowId: $orgRowId) {
        bots(first: null, after: $cursor, filter: $botFilter)
          @connection(key: "Bots_bots", filters: []) {
          edges {
            node {
              id
              rowId
              name
              avatar
              description
              teams {
                totalCount
              }
            }
          }
        }
      }
    }
  `
};

export default createFragmentContainer(
  compose(withRouter, withGqlClient, injectSheet(styles))(Bots), // component
  {
    organization: graphql`
      fragment bots_organization on Organization
      @argumentDefinitions(
        count: { type: "Int" }
        cursor: { type: "Cursor" }
        botFilter: { type: "BotFilter" }
      ) {
        ...botsHeader_organization
        rowId
        account {
          canUpdate
        }
        bots(first: $count, after: $cursor, filter: $botFilter)
          @connection(key: "Bots_bots", filters: []) {
          totalCount
          edges {
            cursor
            node {
              id
              rowId
              name
              avatar
              description
              teams {
                totalCount
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `
  },
  ConnectionConfig
);
